import React, { useEffect, useState } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './Theme';
import './index.css';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Home from './Pages/Home';
import OurPoodles from './Pages/OurPoodles';
import PuppyApplication from './Pages/PuppyApplication';
import UpcomingLitters from './Pages/UpcomingLitters';
import HealthHistory from './Pages/HealthHistory';
import Grooming from './Pages/Grooming';
import AboutUs from './Pages/AboutUs';
import PuppyOwners from './Pages/PuppyOwners';
import AdminHome from './Pages/AdminHome';
import AdminInvite from './Pages/AdminInvite';
import AdminLitters from './Pages/AdminLitters';
import Footer from './components/Footer';
import FloatingNav from './components/FloatingNav';
import NavigationDrawer from './components/NavigationDrawer';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import { PAGE_BAND_COLOR, pageColumnPx } from './theme/pageLayout';
import ReactGA from 'react-ga4'; // Import react-ga4

const AppContent: React.FC = () => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';
    const [menuOpen, setMenuOpen] = useState(false);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    useEffect(() => {
        // Track page views
        ReactGA.send({ hitType: "pageview", page: location.pathname });
    }, [location]); // Run this effect whenever the location changes

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    return (
        <>
            <ScrollToTop />
            <CssBaseline />
            {!isHomePage && (
                <Box
                    sx={{
                        width: '100%',
                        bgcolor: { xs: '#fff', md: PAGE_BAND_COLOR },
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1400,
                            mx: 'auto',
                            px: { xs: 0, md: pageColumnPx.md, lg: pageColumnPx.lg },
                            pt: {
                                xs: 'max(12px, env(safe-area-inset-top))',
                                md: 'max(24px, env(safe-area-inset-top))',
                            },
                            pb: { xs: 0, md: 2 },
                            boxSizing: 'border-box',
                        }}
                    >
                        <FloatingNav
                            variant="interior"
                            menuOpen={menuOpen}
                            onOpenMenu={() => setMenuOpen(true)}
                        />
                    </Box>
                </Box>
            )}
            {!isHomePage && isMobile && (
                <NavigationDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
            )}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                backgroundColor: isHomePage
                    ? 'transparent'
                    : { xs: '#fff', md: PAGE_BAND_COLOR },
                flexGrow: 1,
            }}>
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    width: '100%',
                    px: { xs: 0, md: 0 },
                    py: 0,
                    boxSizing: 'border-box',
                }}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/our-poodles" element={<OurPoodles />} />
                        <Route path="/puppy-application" element={<PuppyApplication />} />
                        <Route path="/upcoming-litters" element={<UpcomingLitters />} />
                        <Route path="/health-history" element={<HealthHistory />} />
                        <Route path="/grooming" element={<Grooming />} />
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route path="/puppy-owners" element={<PuppyOwners />} />
                        <Route
                            path="/sign-in"
                            element={<Navigate to={`/puppy-owners${location.search}`} replace />}
                        />
                        <Route
                            path="/signin"
                            element={<Navigate to={`/puppy-owners${location.search}`} replace />}
                        />
                        <Route element={<ProtectedRoute role="admin" />}>
                            <Route path="/admin" element={<AdminHome />} />
                            <Route path="/admin/litters" element={<AdminLitters />} />
                            <Route
                                path="/admin/owners"
                                element={<Navigate to="/admin/litters" replace />}
                            />
                            <Route path="/admin/invite" element={<AdminInvite />} />
                        </Route>
                    </Routes>
                </Box>
                <Footer />
            </Box>
        </>
    );
};

const App: React.FC = () => (
    <ThemeProvider theme={theme}>
        <AppErrorBoundary>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </AppErrorBoundary>
    </ThemeProvider>
);

export default App;
